import { z } from 'zod';

/**
 * Role w workspace (ARCHITECTURE §5). `client` (DECISIONS 2026-10-17 [P] „Model portalu klienta”): osoba klienta
 * w portalu zgłoszeń, przypięta do jednego klienta (`memberships.client_id`); widzi wyłącznie portal (`portal.*`),
 * nie należy do zespołu (bez Zespołu, wykonawców, AI, MCP i modułów).
 */
export const ROLES = ['owner', 'admin', 'manager', 'user', 'client'] as const;
export type Role = (typeof ROLES)[number];
export const roleSchema = z.enum(ROLES);
/** Role zespołu (wszystkie poza osobą klienta w portalu). */
export const TEAM_ROLES = ['owner', 'admin', 'manager', 'user'] as const satisfies readonly Role[];
export type TeamRole = (typeof TEAM_ROLES)[number];
/** Rola osoby klienta (portal zgłoszeń). */
export const CLIENT_ROLE = 'client' as const satisfies Role;
export function isClientRole(role: string | null | undefined): boolean {
  return role === CLIENT_ROLE;
}

/** Moduły z uprawnieniami. „Dziś” jest zawsze; Spotkania i Oferty należą do `clients`. */
export const MODULES = [
  'inbox',
  'approvals',
  'tasks',
  'clients',
  'projects',
  'time',
  'maintenance',
  'billing',
  'knowledge',
  'agents',
] as const;
export type ModuleKey = (typeof MODULES)[number];
export const moduleSchema = z.enum(MODULES);

/** Poziom dostępu do modułu. */
export const ACCESS_LEVELS = ['none', 'view', 'edit'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];
export const accessLevelSchema = z.enum(ACCESS_LEVELS);
/** Poziom wymagany przez usługę. */
export type RequiredLevel = Exclude<AccessLevel, 'none'>;

/** Flagi logiczne (przełączniki). `manageMembers` wynika z roli (owner/admin), nie jest zapisywana w członkostwie. */
export const BOOLEAN_FLAGS = [
  'seesRates',
  'sendsEmails',
  'preparesBilling',
  'issuesInvoices',
  'deploysProduction',
  'exportsData',
  'vaultAccess',
  'manageMembers',
] as const;
export type BooleanFlag = (typeof BOOLEAN_FLAGS)[number];

/** Wszystkie flagi z ARCHITECTURE §5. */
export const FLAGS = [...BOOLEAN_FLAGS, 'approvesAi', 'aiMonthlyLimitPln'] as const;
export type FlagKey = (typeof FLAGS)[number];

export const APPROVES_AI = ['none', 'own', 'all'] as const;
export type ApprovesAi = (typeof APPROVES_AI)[number];
export const approvesAiSchema = z.enum(APPROVES_AI);

/** Flagi zapisywane w `memberships.flags` (jsonb). */
export interface MembershipFlags {
  seesRates: boolean;
  sendsEmails: boolean;
  approvesAi: ApprovesAi;
  preparesBilling: boolean;
  issuesInvoices: boolean;
  deploysProduction: boolean;
  exportsData: boolean;
  vaultAccess: boolean;
}

export const membershipFlagsSchema = z.object({
  seesRates: z.boolean().default(false),
  sendsEmails: z.boolean().default(false),
  approvesAi: approvesAiSchema.default('none'),
  preparesBilling: z.boolean().default(false),
  issuesInvoices: z.boolean().default(false),
  deploysProduction: z.boolean().default(false),
  exportsData: z.boolean().default(false),
  vaultAccess: z.boolean().default(false),
});

export const DEFAULT_MEMBERSHIP_FLAGS: MembershipFlags = {
  seesRates: false,
  sendsEmails: false,
  approvesAi: 'none',
  preparesBilling: false,
  issuesInvoices: false,
  deploysProduction: false,
  exportsData: false,
  vaultAccess: false,
};

/** Zakres danych użytkownika. */
export const DATA_SCOPES = ['all', 'selected', 'assigned'] as const;
export type DataScope = (typeof DATA_SCOPES)[number];
export const dataScopeSchema = z.enum(DATA_SCOPES);

/** Szablony uprawnień (PERMISSIONS §3.4). */
export const PERMISSION_TEMPLATES = ['developer', 'support', 'accounting', 'partner'] as const;
export type PermissionTemplate = (typeof PERMISSION_TEMPLATES)[number];
export const permissionTemplateSchema = z.enum(PERMISSION_TEMPLATES);

export interface PermissionTemplateDefinition {
  modules: Partial<Record<ModuleKey, AccessLevel>>;
  dataScope: DataScope;
  flags: Partial<MembershipFlags>;
  /**
   * Domyślny limit AI osoby w zł / mies. (`memberships.ai_monthly_limit_pln`); brak = szablon nie ustawia limitu.
   * `'0'` = AI osoby wyłączone w tym workspace'ie (DECISIONS 2026-10-08 [S] „Reguła partnerów”).
   */
  aiMonthlyLimitPln?: string;
}

/** Punkt startowy szablonów; po wybraniu można zmienić pojedyncze moduły, klientów i przełączniki. */
export const PERMISSION_TEMPLATE_DEFAULTS: Record<PermissionTemplate, PermissionTemplateDefinition> = {
  developer: {
    // Klienci: Podgląd (DECISIONS [P] „Rozstrzygnięcia pytań z Fali 1”): karta klienta swoich projektów (tryb częściowy).
    modules: {
      tasks: 'edit',
      projects: 'edit',
      time: 'edit',
      knowledge: 'edit',
      agents: 'edit',
      maintenance: 'view',
      clients: 'view',
    },
    dataScope: 'assigned',
    flags: { seesRates: false, deploysProduction: false },
  },
  support: {
    modules: { inbox: 'edit', clients: 'edit', tasks: 'edit', time: 'edit', knowledge: 'view' },
    dataScope: 'selected',
    flags: { sendsEmails: true, seesRates: false },
  },
  accounting: {
    modules: { billing: 'edit', time: 'view', clients: 'view', maintenance: 'view' },
    dataScope: 'all',
    flags: { seesRates: true, preparesBilling: true, issuesInvoices: false },
  },
  /**
   * Partner (DECISIONS 2026-10-08 „Współpraca z Marcinem: dwa workspace'y”): osoba z innej firmy (zwykle właściciel
   * własnego workspace'u) do wspólnych projektów. Tylko projekty, w których jest członkiem (karta klienta tych
   * projektów w trybie częściowym, bez kwot); praca w Projektach, Zadaniach, Czasie i Wiedzy; Inbox: Podgląd
   * (powiadomienia o jego zadaniach, odczytanie). Bez Rozliczeń (kwoty prowadzącego), Utrzymania (umowy, koszty,
   * sejf klienta), Agentów (koszt AI i dostęp do repozytoriów prowadzącego) i Akceptacji (decyzje prowadzącego).
   * Wszystkie przełączniki wyłączone — swoją stawkę partner widzi przy własnych wpisach (osobna reguła modułu Czas).
   * Limit AI 0 zł (DECISIONS 2026-10-08 [S] „Reguła partnerów: osobne firmy”): partner korzysta z AI we własnym
   * workspace'ie (własny klucz i budżet), nie na kluczu gospodarza; gospodarz może świadomie podnieść limit w Zespole.
   */
  partner: {
    modules: {
      projects: 'edit',
      tasks: 'edit',
      time: 'edit',
      knowledge: 'edit',
      clients: 'view',
      inbox: 'view',
    },
    dataScope: 'assigned',
    flags: {},
    aiMonthlyLimitPln: '0',
  },
};

/** Typy zasobów w `access_grants`. */
export const GRANT_RESOURCE_TYPES = ['client', 'project', 'asset', 'vault_asset'] as const;
export type GrantResourceType = (typeof GRANT_RESOURCE_TYPES)[number];
export const grantResourceTypeSchema = z.enum(GRANT_RESOURCE_TYPES);

export const GRANT_LEVELS = ['view', 'edit'] as const;
export type GrantLevel = (typeof GRANT_LEVELS)[number];

export const MEMBERSHIP_STATUSES = ['active', 'disabled'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/** Kanał, którym działa aktor (UI, AI, MCP, worker, system). */
export const VIAS = ['ui', 'ai', 'mcp', 'worker', 'system'] as const;
export type Via = (typeof VIAS)[number];
export const viaSchema = z.enum(VIAS);

/** Typ aktora w audit_log. */
export const ACTOR_TYPES = ['user', 'ai', 'mcp', 'worker', 'system'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

/** Ważność zdarzeń (Inbox, push). */
export const SEVERITIES = ['critical', 'approval', 'normal', 'fyi'] as const;
export type Severity = (typeof SEVERITIES)[number];
export const severitySchema = z.enum(SEVERITIES);

/** `simulation` = zdarzenie z narzędzia dev „Symuluj zdarzenie” (Inbox, tylko NODE_ENV=development). */
export const EVENT_SOURCES = ['ui', 'ai', 'mcp', 'worker', 'integration', 'monitor', 'simulation'] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

export const INBOX_STATUSES = ['unread', 'read', 'done'] as const;
export type InboxStatus = (typeof INBOX_STATUSES)[number];

export const APPROVAL_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'expired',
  'executing',
  'succeeded',
  'failed',
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const APPROVAL_INITIATOR_TYPES = ['user', 'ai', 'mcp', 'worker'] as const;
export type ApprovalInitiatorType = (typeof APPROVAL_INITIATOR_TYPES)[number];

/** Rodzaje tokenów jednorazowych. `email_2fa` dopiero w etapie końcowym (2FA). */
export const AUTH_TOKEN_KINDS = ['password_reset', 'email_2fa'] as const;
export type AuthTokenKind = (typeof AUTH_TOKEN_KINDS)[number];

/**
 * Rodzaje tokenów API (Fala 5, `@fos/shared/contracts/tokens`): `pat` = osobisty token MCP (`fos_pat_…`),
 * `runner` = token lokalnego runnera (`fos_rnr_…`, tylko `/api/runner/*`), `run` = krótko żyjący token przebiegu
 * (`fos_run_…`, MCP z zakresem projektu runu i tylko narzędziami runu), `oauth` = połączenie konektora (Claude przez
 * OAuth 2.1, 9.10.2026): zgoda osoby w aplikacji; tokeny dostępu `fos_oat_…` i odświeżania `fos_ort_…` w `oauth_tokens`.
 */
export const API_TOKEN_KINDS = ['pat', 'runner', 'run', 'oauth'] as const;
export type ApiTokenKind = (typeof API_TOKEN_KINDS)[number];

export const API_TOKEN_ACCESS = ['read', 'write'] as const;
export type ApiTokenAccess = (typeof API_TOKEN_ACCESS)[number];

export const APPROVAL_PUSH_MODES = ['never', 'high', 'always'] as const;
export type ApprovalPushMode = (typeof APPROVAL_PUSH_MODES)[number];

export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

export const WORKSPACE_PLANS = ['internal', 'pilot', 'standard'] as const;
export type WorkspacePlan = (typeof WORKSPACE_PLANS)[number];
